import { useEffect, useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getReportById, getReportAnalytics } from 'common/src/api/reports';
import { Report, ReportAnalytics } from 'common/src/utils/types';
import { format } from 'date-fns';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import Header from '../../../components/layouts/Header';
import {
  BackButton,
  KpiCard,
  LoadingState,
  PageLayout,
} from '../../../components/ui';

const ReportAnalyticsPage = () => {
  const { darkMode } = useUserStore();
  const [, params] = useRoute('/superadmin-reports/analytics/:id');
  const reportId = params?.id ? parseInt(params.id) : null;
  const [, setLocation] = useLocation();

  const [report, setReport] = useState<Report | null>(null);
  const [analytics, setAnalytics] = useState<ReportAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reportId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [reportData, analyticsData] = await Promise.all([
          getReportById(reportId),
          getReportAnalytics(reportId),
        ]);

        if (!reportData) {
          toast.error('Report not found');
          setLocation('/superadmin-reports');
          return;
        }

        setReport(reportData);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error loading analytics:', error);
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [reportId, setLocation]);

  if (loading) {
    return (
      <LoadingState label="Loading analytics..." />
    );
  }

  if (!report || !analytics) {
    return null;
  }

  // Chart configurations
  const viewsOverTimeOptions: ApexOptions = {
    chart: {
      type: 'line',
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    xaxis: {
      categories: analytics.views_over_time.map((item) => item.date),
      labels: {
        rotate: -45,
        style: { fontSize: '12px' },
      },
    },
    yaxis: {
      title: { text: 'Views' },
    },
    colors: ['#BE123C'],
  };

  const viewsOverTimeSeries = [
    {
      name: 'Views',
      data: analytics.views_over_time.map((item) => item.count),
    },
  ];

  const sharesByMethodOptions: ApexOptions = {
    chart: {
      type: 'donut',
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: { show: false },
    },
    labels: analytics.shares_by_method.map((item) => item.method),
    legend: { position: 'bottom' },
    colors: ['#BE123C', '#DC2626', '#F59E0B'],
  };

  const sharesByMethodSeries = analytics.shares_by_method.map(
    (item) => item.count
  );

  return (
    <PageLayout>
      <BackButton href="/superadmin-reports" label="Back to Reports" />
      <Header title={report.title} description="Analytics Dashboard" />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Total Views" value={analytics.total_views} />
        <KpiCard label="Unique Viewers" value={analytics.unique_views} />
        <KpiCard label="Total Shares" value={analytics.shares_count} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Views Over Time</h3>
          </CardHeader>
          <CardBody>
            <ReactApexChart
              options={viewsOverTimeOptions}
              series={viewsOverTimeSeries}
              type="line"
              height={300}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Shares by Method</h3>
          </CardHeader>
          <CardBody className="flex justify-center">
            {analytics.shares_by_method.length > 0 ? (
              <ReactApexChart
                options={sharesByMethodOptions}
                series={sharesByMethodSeries}
                type="donut"
                height={300}
              />
            ) : (
              <div className="text-center text-default-500 py-8">
                No shares yet
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views by User */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Views by User</h3>
          </CardHeader>
          <CardBody>
            <Table aria-label="Views by user">
              <TableHeader>
                <TableColumn>USER</TableColumn>
                <TableColumn>VIEWS</TableColumn>
              </TableHeader>
              <TableBody
                items={analytics.views_by_user.slice(0, 10)}
                emptyContent="No user views recorded"
              >
                {(item) => (
                  <TableRow key={item.user_id}>
                    <TableCell>{item.user_name}</TableCell>
                    <TableCell>{item.count}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        {/* Views by Exporter */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Views by Exporter</h3>
          </CardHeader>
          <CardBody>
            <Table aria-label="Views by exporter">
              <TableHeader>
                <TableColumn>EXPORTER</TableColumn>
                <TableColumn>VIEWS</TableColumn>
              </TableHeader>
              <TableBody
                items={analytics.views_by_exporter}
                emptyContent="No exporter views recorded"
              >
                {(item) => (
                  <TableRow key={item.exporter_id}>
                    <TableCell>{item.exporter_name}</TableCell>
                    <TableCell>{item.count}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </PageLayout>
  );
};

export default ReportAnalyticsPage;
